import React from "react";
import LegalLayout, { H2, H3, P, UL, LI, CALLOUT } from "../components/LegalLayout";
import { CardIllustration } from "../components/legal/LegalIllustrations";

const SECTIONS = [
  { id: "what", labelFr: "Ce qui peut être payant", labelEn: "What can involve payment" },
  { id: "commission", labelFr: "Commission et TVA", labelEn: "Commission and VAT" },
  { id: "escrow", labelFr: "Séquestre", labelEn: "Escrow" },
  { id: "disputes", labelFr: "Signalement et litiges", labelEn: "Reports and disputes" },
  { id: "payouts", labelFr: "Recevoir un paiement", labelEn: "Receiving a payment" },
  { id: "receipts", labelFr: "Reçus et virements", labelEn: "Receipts and payouts" },
  { id: "cancellation", labelFr: "Annulation et double réservation", labelEn: "Cancellation and double-booking" },
];

export default function PaymentsPolicy() {
  return (
    <LegalLayout
      titleFr="Paiements et commissions" titleEn="Payments and Commissions"
      taglineFr="Comment marchent les paiements sur OLTODO, du prélèvement au virement."
      taglineEn="How payments work on OLTODO, from charge to payout."
      updatedAt="7 septembre 2026"
      illustration={<CardIllustration />}
      sections={SECTIONS}
      tldrFr={[
        "Tous les paiements sont traités par Stripe, jamais par OLTODO directement, ta carte n'est jamais stockée sur nos serveurs.",
        "OLTODO prélève une commission de 10 % hors taxes sur les transactions, plus la TVA applicable.",
        "L'argent reste séquestré 72 heures après l'activité, ou 7 jours après l'achat d'un article, le temps qu'un problème puisse être signalé.",
        "Tu reçois un reçu par email à chaque achat, et un email dès qu'un virement t'est envoyé.",
      ]}
      tldrEn={[
        "All payments are processed by Stripe, never by OLTODO directly, your card is never stored on our servers.",
        "OLTODO takes a 10% commission (excluding tax) on transactions, plus applicable VAT.",
        "Funds stay in escrow for 72 hours after the activity, or 7 days after buying an item, so a problem can be reported.",
        "You get an email receipt for every purchase, and an email as soon as a payout is sent to you.",
      ]}
    >
      {(lang) => lang === "fr" ? (
        <>
          <P>
            Certaines fonctionnalités d'OLTODO donnent lieu à un paiement réel, entre membres ou vers un
            professionnel. Cette page détaille exactement comment cet argent circule, qui le reçoit, et quand,
            en complément des Conditions Générales d'Utilisation.
          </P>

          <H2 id="what">1. Ce qui peut être payant</H2>
          <UL>
            <LI><strong>Participation à une activité</strong>, quand l'organisateur a fixé un prix pour rejoindre sa Passion.</LI>
            <LI><strong>Prestations Power</strong>, un service rendu par un membre (créneau réservé auprès d'un prestataire).</LI>
            <LI><strong>Réservations de lieu</strong>, un créneau ou une prestation proposée par un lieu partenaire.</LI>
            <LI><strong>Articles du catalogue d'un lieu</strong>, achetés directement dans l'application.</LI>
            <LI><strong>Move</strong>, une place dans un trajet partagé (covoiturage, vélo) quand elle est payante.</LI>
          </UL>
          <P>Toutes ces transactions sont traitées par notre prestataire de paiement, Stripe. Ta carte bancaire est
          saisie directement sur les serveurs de Stripe, jamais sur ceux d'OLTODO, qui ne voit et ne conserve
          jamais ton numéro de carte complet.</P>

          <H2 id="commission">2. Commission et TVA</H2>
          <P>
            OLTODO prélève automatiquement une commission de <strong>10 % hors taxes</strong> sur le montant de
            chaque transaction, à laquelle s'ajoute la TVA applicable lorsqu'elle est due, selon le taux en
            vigueur pour le type de prestation ou d'article concerné, conformément à la réglementation fiscale
            française. Cette commission finance l'hébergement, la modération, la vérification d'identité et le
            développement du service.
          </P>
          <CALLOUT>
            Exemple : pour une prestation à 50&nbsp;€, l'acheteur paie 50&nbsp;€, OLTODO retient 5&nbsp;€ de
            commission (10&nbsp;% hors taxes) plus la TVA applicable, et le solde est viré au prestataire une
            fois le délai de séquestre écoulé.
          </CALLOUT>

          <H2 id="escrow">3. Séquestre</H2>
          <P>L'argent versé par l'acheteur n'est pas immédiatement disponible pour le vendeur ou le prestataire.
          Il reste séquestré chez Stripe pendant un délai qui dépend du type de transaction :</P>
          <UL>
            <LI><strong>72 heures</strong> après la tenue de l'activité ou du rendez-vous réservé (participation, Power, réservation de lieu).</LI>
            <LI><strong>7 jours</strong> après l'achat, pour un article du catalogue d'un lieu.</LI>
          </UL>
          <P>Pendant ce délai, l'acheteur peut signaler un problème directement depuis l'application (bouton
          Signaler sur la transaction concernée). Passé ce délai, si aucun signalement n'a été fait, le virement
          se déclenche automatiquement.</P>

          <H2 id="disputes">4. Signalement et litiges</H2>
          <P>Si un problème survient (activité qui n'a pas eu lieu, prestation non conforme, article non reçu),
          l'acheteur peut signaler le paiement concerné pendant le délai de séquestre. Cela :</P>
          <UL>
            <LI>Suspend immédiatement le virement au vendeur, le temps de la vérification.</LI>
            <LI>Alerte l'équipe OLTODO, qui examine le signalement.</LI>
            <LI>Aboutit soit à un remboursement de l'acheteur, soit à une levée du blocage si le signalement n'est pas fondé.</LI>
          </UL>
          <P>L'équipe OLTODO peut également, de sa propre initiative, mettre un paiement en attente le temps d'une
          vérification, par exemple suite à un signalement reçu ailleurs dans l'application.</P>

          <H2 id="payouts">5. Recevoir un paiement en tant que vendeur ou prestataire</H2>
          <P>
            Pour recevoir de l'argent sur OLTODO, il faut avoir terminé l'inscription bancaire requise par notre
            partenaire Stripe Connect (informations d'identité et coordonnées bancaires, gérées entièrement par
            Stripe). Tant que cette inscription n'est pas terminée, les sommes dues restent en attente et sont
            transférées automatiquement dès que l'inscription est complétée.
          </P>

          <H2 id="receipts">6. Reçus et confirmations par email</H2>
          <P>Pour que chaque transaction laisse une trace écrite claire, OLTODO envoie automatiquement :</P>
          <UL>
            <LI>Un <strong>reçu d'achat</strong> à l'acheteur, dès que le paiement est confirmé.</LI>
            <LI>Une <strong>confirmation de réservation</strong> détaillée, pour une réservation de lieu.</LI>
            <LI>Une notification de <strong>devis accepté</strong>, quand une proposition de prestation est validée.</LI>
            <LI>Un email dès qu'un <strong>virement est effectué</strong> sur le compte bancaire du vendeur ou du prestataire.</LI>
            <LI>Un email en cas de <strong>remboursement</strong>, ou si un paiement est mis en vérification.</LI>
          </UL>
          <P>Ces emails s'ajoutent aux notifications déjà visibles dans l'application, et à l'historique complet
          des transactions consultable depuis les réglages.</P>

          <H2 id="cancellation">7. Annulation et double réservation</H2>
          <P>
            Si un organisateur annule une activité payante, les participants déjà inscrits sont automatiquement
            remboursés. Si deux personnes parviennent à réserver le même créneau au même moment (rare, mais
            possible sur un système distribué), la seconde confirmation est automatiquement remboursée
            intégralement, avec un email de confirmation, sans qu'aucune démarche ne soit nécessaire de sa part.
          </P>
        </>
      ) : (
        <>
          <P>
            Some OLTODO features involve a real payment, between members or to a professional. This page details
            exactly how that money moves, who receives it, and when, alongside the Terms of Use.
          </P>

          <H2 id="what">1. What Can Involve Payment</H2>
          <UL>
            <LI><strong>Joining an activity</strong>, when the organizer has set a price to join their Passion.</LI>
            <LI><strong>Power services</strong>, a service provided by a member (a booked slot with a provider).</LI>
            <LI><strong>Venue bookings</strong>, a slot or service offered by a partner venue.</LI>
            <LI><strong>Venue catalog items</strong>, purchased directly in the app.</LI>
            <LI><strong>Move</strong>, a spot in a shared ride (carpool, bike) when it's paid.</LI>
          </UL>
          <P>All these transactions are processed by our payment provider, Stripe. Your card details are entered
          directly on Stripe's servers, never on OLTODO's, and OLTODO never sees or keeps your full card number.</P>

          <H2 id="commission">2. Commission and VAT</H2>
          <P>
            OLTODO automatically deducts a <strong>10% commission (excluding tax)</strong> from the amount of
            each transaction, plus applicable VAT where due, at the rate in force for the type of service or
            item concerned, in accordance with French tax regulations. This commission funds hosting, moderation,
            identity verification and the development of the service.
          </P>
          <CALLOUT>
            Example: for a €50 service, the buyer pays €50, OLTODO keeps €5 in commission (10% excluding tax)
            plus applicable VAT, and the balance is transferred to the provider once the escrow period has
            elapsed.
          </CALLOUT>

          <H2 id="escrow">3. Escrow</H2>
          <P>Money paid by a buyer isn't immediately available to the seller or provider. It's held in escrow by
          Stripe for a period that depends on the type of transaction:</P>
          <UL>
            <LI><strong>72 hours</strong> after the activity or booked appointment takes place (participation, Power, venue booking).</LI>
            <LI><strong>7 days</strong> after purchase, for a venue catalog item.</LI>
          </UL>
          <P>During this period, the buyer can report a problem directly from the app (a Report button on the
          transaction). After this period, if no report has been made, the payout is triggered automatically.</P>

          <H2 id="disputes">4. Reports and Disputes</H2>
          <P>If a problem occurs (the activity didn't take place, the service wasn't as described, the item wasn't
          received), the buyer can report the payment during the escrow period. This:</P>
          <UL>
            <LI>Immediately suspends the payout to the seller, pending review.</LI>
            <LI>Alerts the OLTODO team, who review the report.</LI>
            <LI>Results in either a refund to the buyer, or the hold being lifted if the report isn't upheld.</LI>
          </UL>
          <P>The OLTODO team can also, on its own initiative, put a payment on hold pending review, for example
          following a report received elsewhere in the app.</P>

          <H2 id="payouts">5. Receiving a Payment as a Seller or Provider</H2>
          <P>
            To receive money on OLTODO, you need to complete the bank onboarding required by our partner Stripe
            Connect (identity and bank details, handled entirely by Stripe). Until this onboarding is complete,
            amounts owed remain pending and are transferred automatically as soon as onboarding is finished.
          </P>

          <H2 id="receipts">6. Receipts and Email Confirmations</H2>
          <P>So every transaction leaves a clear written trace, OLTODO automatically sends:</P>
          <UL>
            <LI>A <strong>purchase receipt</strong> to the buyer, as soon as payment is confirmed.</LI>
            <LI>A detailed <strong>booking confirmation</strong>, for a venue booking.</LI>
            <LI>An <strong>offer accepted</strong> notification, when a service proposal is confirmed.</LI>
            <LI>An email as soon as a <strong>payout is sent</strong> to the seller's or provider's bank account.</LI>
            <LI>An email in case of a <strong>refund</strong>, or if a payment is put under review.</LI>
          </UL>
          <P>These emails come on top of the notifications already visible in the app, and the full transaction
          history available from settings.</P>

          <H2 id="cancellation">7. Cancellation and Double-Booking</H2>
          <P>
            If an organizer cancels a paid activity, participants already registered are automatically refunded.
            If two people manage to book the same slot at the same time (rare, but possible on a distributed
            system), the second confirmation is automatically refunded in full, with a confirmation email, with
            no action needed on their part.
          </P>
        </>
      )}
    </LegalLayout>
  );
}
